// The Library's address: which segment, and inside Catalog which sub-tab, the
// URL names. One place reads it and one place builds it, so a redirect, a link
// and the page itself cannot disagree about what `?seg=` / `?sub=` mean.
//
// ADR-106 Decision 1 gives every stop of the swipe chain its own address, and
// its prerequisite P1 puts Catalog's sub-tab in the URL. Two rules follow from
// the owner decisions: the URL and the visible tab never disagree (no `sub` on
// Mine, which has no sub-tabs; no stale `sub` after a tap), and a missing or
// unknown sub-tab lands on Decks.

export type LibrarySeg = 'mine' | 'catalog'
export type CatalogSub = 'decks' | 'courses' | 'explore'

const CATALOG_SUBS: readonly CatalogSub[] = ['decks', 'courses', 'explore']

export function isCatalogSub(value: string | null): value is CatalogSub {
  return value !== null && (CATALOG_SUBS as readonly string[]).includes(value)
}

export interface LibraryAddress {
  seg: LibrarySeg
  /** The Catalog sub-tab; `null` on Mine. */
  sub: CatalogSub | null
  /**
   * The canonical form of the given params when they differ from it, else
   * `null`. The page replaces the URL with it so what is shown and what the
   * address says are the same thing.
   */
  canonical: URLSearchParams | null
}

export function resolveLibraryAddress(params: URLSearchParams): LibraryAddress {
  const rawSeg = params.get('seg')
  const rawSub = params.get('sub')

  // No `?seg=` at all lands on the reader's own decks (see LibraryPage for why
  // that default is not Catalog). `explore` was once a Library segment: an old
  // bookmark or link carrying it means Catalog › Explore, not Catalog › Decks.
  // Any other unrecognised value clamps to Catalog, as it always has.
  const seg: LibrarySeg = rawSeg === null || rawSeg === 'mine' ? 'mine' : 'catalog'

  const sub: CatalogSub | null =
    seg === 'mine' ? null
    : isCatalogSub(rawSub) ? rawSub
    : rawSeg === 'explore' ? 'explore'
    : 'decks'

  const next = new URLSearchParams(params)
  if (seg === 'mine') {
    // A missing `seg` stays missing — it already means Mine.
    if (rawSeg !== null) next.set('seg', 'mine')
    next.delete('sub')
  } else {
    next.set('seg', 'catalog')
    next.set('sub', sub as CatalogSub)
  }

  const canonical = next.toString() === params.toString() ? null : next
  return { seg, sub, canonical }
}

/** Builds a Library URL. `sub` is ignored for Mine, which has no sub-tabs. */
export function libraryHref(seg: LibrarySeg, sub: CatalogSub = 'decks'): string {
  return seg === 'mine' ? '/library?seg=mine' : `/library?seg=catalog&sub=${sub}`
}
